import { Component, OnInit, AfterViewInit } from '@angular/core';
import { ViewChild, ElementRef } from '@angular/core';


// Angular material components
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { MatIconModule } from '@angular/material/icon';
import { MatDialog } from '@angular/material/dialog';


// Custom components
import { WaitingSpinnerComponent } from '../../utils/waiting-spinner/waiting-spinner.component';
import { AdditionalDataPopupComponent } from '../../popups/additional-data-popup/additional-data-popup.component';
import { ModifyPopupComponent } from '../../popups/modify-popup/modify-popup.component';


// Services
import { LanguageService } from '../../../services/language/language.service';
import { GlobalVariables } from '../../../constants/global';
import { UtilsService } from '../../../services/utils/utils.service';
import { DicomService } from '../../../services/dicom/dicom.service';


@Component({
  selector: 'app-landing-page',
  standalone: true,
  imports: [
    // Angular modules
    CommonModule, 
    // Material design modules
    MatButtonModule,
    MatMenuModule,
    MatIconModule,
    // Custom modules
    WaitingSpinnerComponent
  ],
  templateUrl: './landing-page.component.html',
  styleUrl: './landing-page.component.css'
})
export class LandingPageComponent implements OnInit {

  displayWaitingSpiner:boolean = false
  waitingMessage:string = ''
  displayCanvas:boolean = false
  applicationState:number = 0

  additionalInformationDict:any = {}

  classMapOpacity:number = 0.5

  urlImagePng:string = ''
  urlClassMapPng:string = ''
  urlClassMapBin:string = ''

  private _defaultImageSize = {width: 512, height: 512}

  @ViewChild('canvasElement') canvasElement!: ElementRef;
  @ViewChild('canvasElement_Overlay') canvasElement_Overlay!: ElementRef;


  constructor(
    public languageService:LanguageService,
    public utilsService:UtilsService,
    public dicomService:DicomService,
    public dialog: MatDialog,
  ){}


  ngOnInit(): void {
    // Prevent context menu option for right click (download image as...)
    document.addEventListener('contextmenu', function(event) {
      event.preventDefault();
    });
  }

  ngAfterViewInit(): void {
      this.setApplicationState(0)
  }

  setApplicationState(state:number){
    // Reset the necessary view items
    this.resetView(state)
    // Reset the necessary component variables
    this.resetVariables(state)
    // State 0: Once inside the page...
    if(state == 0){
      this.initCanvasSizes()
    }
    // State 1: Once the image is loaded...
    else if(state == 1){
      
    }
    // State 2: Once the image has been classified...
    else if(state == 2){
      
    }
    // Store the current application state
    this.applicationState = state
  }

  resetView(state:number){
    // State 0: Before loading the image...
    if(state <= 0){
      this.clearAllCanvas()
    }
    // State 1: Before uploading the classified image...
    if(state <= 1){
      this.clearCanvasOverlay()
    }
  }

  resetVariables(state:number){
    // State 1: Before loading the image...
    if(state <= 0){
      this.urlImagePng = ''
      this.urlClassMapPng = ''
      this.classMapOpacity = 0.5
      this.additionalInformationDict = {}
    }
  }



  async onFileSelected_DICOM(event:any){
    // Update application state
    this.setApplicationState(0)
    // Get the file array (make a copy so the event can be reset to its default value)
    let fileArray:File[] = Array.from(event.target.files);
    // Verify that there is at least one file selected
    if(fileArray.length == 0) return
    // Display waiting spinner
    this.waitingMessage = this.languageService.Strings.appStrings.waitingMessageLoadDicom
    this.displayWaitingSpiner = true
    // Load DICOM image
    let resultsDict = await this.dicomService.loadNewFile(fileArray[0])
    if(!resultsDict['resultOk']){ 
      this.displayWaitingSpiner = false
      if(resultsDict['error'] == 'The loaded DICOM does not have "cm" units'){
        this.utilsService.displaySnackBar(this.languageService.Strings.errors.error_dicomNotInCm)
      } else {
        this.utilsService.displaySnackBar(resultsDict['error'])
      }
      return
    }
    // Save download URL of DICOM image
    this.urlImagePng = resultsDict['pngUrl']
    // Display PNG image
    this.displayImage()
    // Stop waiting spinner
    this.displayWaitingSpiner = false
    // Reset the event to its default value
    event.target.value = '';
    // Update application state
    this.setApplicationState(1)
  }


  async buttonClassify(){
    // Update application state
    this.setApplicationState(1)
    // Display waiting spinner
    this.waitingMessage = this.languageService.Strings.appStrings.waitingMessageClassifyDicom
    this.displayWaitingSpiner = true
    // Send to classify
    let resultsDict = await this.dicomService.classifyDicom()
    if(!resultsDict['resultOk']){ 
      this.displayWaitingSpiner = false
      this.utilsService.displaySnackBar(resultsDict['error'])
      return
    }
    // Save download URL of class map (PNG and binary)
    this.urlClassMapPng = resultsDict['urlClassMapPng']
    this.urlClassMapBin = resultsDict['urlClassMapBin']
    // Display PNG mask
    await this.displayClassMap()
    // Stop waiting spinner
    this.displayWaitingSpiner = false
    // Update application state
    this.setApplicationState(2)
  }


  async buttonData(){
    // Update application state
    this.setApplicationState(2)
    // Open popup of additional data
    this.dialog.open(
      AdditionalDataPopupComponent,
      {
        disableClose: true,
        data: {additionalInformationDict: this.additionalInformationDict},
        width: '500px',   
      } 
    ).afterClosed().subscribe(result => this.shouldUpdateAdditionalInformationDict(result))
  }


  async buttonModify(){
    // Get width and height of the class map
    const { img, width, height } = await this.loadImage(this.urlImagePng);
    // Get aspect ratio
    const aspectRatio = width/height
    // Fetch binary class map
    const classMapBin = await this.utilsService.fetchBinaryFile(this.urlClassMapBin)
    // Open popup of modify
    this.dialog.open(
      ModifyPopupComponent, 
      {
        disableClose: true,
        data: { urlImagePng: this.urlImagePng, imageWidth: width, imageHeight: height, classMapBin: classMapBin},
        width: '100vw',   
        maxWidth: '100vw', 
        height: '100vh',
      } 
    ).afterClosed().subscribe(result => this.shouldUpdateClassMap(result))
  }


  async buttonResults(){
    this.utilsService.displayPatientIdPopup(this.setPatientIdForResultsCallback.bind(this))
  }


  // ----- Internal functions ----- //


  initCanvasSizes(){
    // Canvas
    const canvas = this.canvasElement.nativeElement
    canvas.width = this._defaultImageSize.width;  
    canvas.height = this._defaultImageSize.height; 
    canvas.style.width = `${canvas.width}px`;  
    canvas.style.height = `${canvas.height}px`;

    // Canvas Overlay
    const canvasOverlay = this.canvasElement_Overlay.nativeElement
    canvasOverlay.width = this._defaultImageSize.width;  
    canvasOverlay.height = this._defaultImageSize.height; 
    canvasOverlay.style.width = `${canvasOverlay.width}px`;  
    canvasOverlay.style.height = `${canvasOverlay.height}px`;
  }


  loadImage(src: string): Promise<{ img: HTMLImageElement, width: number, height: number }> {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.src = src;
        img.onload = () => resolve({ img, width: img.width, height: img.height });
        img.onerror = err => reject(err);
    });
  }


  async displayImage(){
    const canvas = this.canvasElement.nativeElement;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.fillStyle = "black";
      try {
        const { img, width, height } = await this.loadImage(this.urlImagePng);
        const aspectRatio = width/height
        this.resizeAndCenterCanvas(canvas, aspectRatio)
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      } catch (err) {
        console.error(`Error displaying DICOM image: ${err}`);
      }
    }
  }


  async displayClassMap(){
    const canvas = this.canvasElement_Overlay.nativeElement;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.fillStyle = "black";
      try {
        const { img, width, height } = await this.loadImage(this.urlClassMapPng);
        const aspectRatio = width/height
        this.resizeAndCenterCanvas(canvas, aspectRatio)
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      } catch (err) {
          console.error(`Error displaying image mask: ${err}`);
      }
      canvas.style.opacity = this.classMapOpacity.toString();
    }
  }


  updateClassMapOpacity(increment:number){
    this.classMapOpacity = Math.min(1, Math.max(0, this.classMapOpacity + increment))
    const canvas = this.canvasElement_Overlay.nativeElement;
    canvas.style.opacity = this.classMapOpacity.toString();
  }


  shouldUpdateAdditionalInformationDict(result){
    if(result['shouldUpdateAdditionalData']){
      this.additionalInformationDict = this.utilsService.deepCopyDict(result['additionalInformationDict']) 
    }   
  }


  resizeAndCenterCanvas(canvas: HTMLCanvasElement, aspectRatio:number){
    // Resize
    canvas.width = this._defaultImageSize.width * aspectRatio
    canvas.height = this._defaultImageSize.height
    canvas.style.width = `${canvas.width}px`;  
    canvas.style.height = `${canvas.height}px`;
    // Center
    canvas.style.display = 'block';
    canvas.style.margin = '0 auto';
    canvas.style.position = 'absolute';
    canvas.style.top = '50%';
    canvas.style.left = '50%';
    canvas.style.transform = 'translate(-50%, -50%)';  
  }

  async shouldUpdateClassMap(result:any){
    if(result['shouldUpdateClassMap']){
      this.waitingMessage = this.languageService.Strings.appStrings.waitingMessageModifyingData
      this.displayWaitingSpiner = true
      // Send to modify class map
      const classMapBin = result['classMapBin']
      const imageWidth = result['imageWidth']
      const imageHeight = result['imageHeight']
      this.dicomService.modifyClassMap(classMapBin, imageWidth, imageHeight, this.modifyCallback.bind(this));
    }
  }

  async modifyCallback(resultsDict){
    if(!resultsDict['resultOk']){ 
      this.displayWaitingSpiner = false
      this.utilsService.displaySnackBar(resultsDict['error'])
      return
    }
    // Update download URL of class map (PNG and binary)
    this.urlClassMapPng = resultsDict['urlClassMapPng']
    this.urlClassMapBin = resultsDict['urlClassMapBin']
    // Display PNG mask
    await this.displayClassMap()
    // Stop waiting spinner
    this.displayWaitingSpiner = false
  }

  setPatientIdForResultsCallback(resultsDict){
    // If button cancel was clicked, return
    if(!resultsDict['shouldGenerateResults']) return
    // Get user-defined patient IP
    let proposedPatientId = resultsDict['proposedPatientId']
    // Get user-defined study IP
    let proposedStudyId = resultsDict['proposedStudyId']
    // Display the waiting popup
    this.waitingMessage = this.languageService.Strings.appStrings.waitingMessageGeneratingResults
    this.displayWaitingSpiner = true
    // Execute results Cloud Run function
    this.dicomService.generateResults(proposedPatientId, proposedStudyId, this.additionalInformationDict, this.generateResults.bind(this))
  }

  generateResults(resultsDict){
    // If the result is not ok 
    if(!resultsDict['resultOk']){
      this.displayWaitingSpiner = false
      this.utilsService.displaySnackBar(resultsDict['error'])
      return
    }
    // If the result is ok, go on
    let downloadUrlList = resultsDict['downloadUrlList']
    let resultsFilenameList = resultsDict['resultFilenameList']
    // Stop waiting popup
    this.displayWaitingSpiner = false
    // Download the files
    for(let index=0; index<downloadUrlList.length; index++){
      this.utilsService.downloadFile(downloadUrlList[index], resultsFilenameList[index])
    } 
  }


  // ----- Actions ----- // 

  
  canvas_onWheel(event:any){
    if (this.applicationState == 2){
      if(event.deltaY > 0) this.updateClassMapOpacity(+0.1)
      if(event.deltaY < 0) this.updateClassMapOpacity(-0.1)
    }
  }

  clearAllCanvas(){
    this.clearCanvas()
    this.clearCanvasOverlay()
  }

  clearCanvas(){
    const canvas = this.canvasElement.nativeElement;
    let ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  }

  clearCanvasOverlay(){
    const canvas = this.canvasElement_Overlay.nativeElement;
    let ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  }

}
